import { Select } from "./Select";
import { useEffect, useRef, useState } from "react";
import {
  defaultAdjustments,
  mapDefaultAddress,
  mapPartValue,
  addressParts,
  coordinateParts,
  coordinatesLocation,
  type Field,
  type MapLocation,
  type MapFormServices,
  type MapSearchResult,
  type Values,
} from "@proof/core";
export function MapPicker({
  field,
  values = {},
  services,
  scope: requestScope,
  disabled,
  onChange,
}: {
  field: Field;
  values?: Values;
  services: MapFormServices;
  scope: string;
  disabled: boolean;
  onChange: (value: Values[string]) => void;
}) {
  const defaultsKey = JSON.stringify([
    field.id,
    field.map,
    [...addressParts, ...coordinateParts].map(({ key }) =>
      mapPartValue(field, values, key),
    ),
  ]);
  const [partOverride, setPartOverride] = useState<{
    base: string;
    answers: Record<string, string>;
  }>();
  const answers =
    partOverride?.base === defaultsKey ? partOverride.answers : {};
  const coordinatesEnabled = !["latitude", "longitude"].some((key) =>
    field.map?.disabledParts?.includes(key),
  );
  const modeKey = JSON.stringify([
    field.id,
    field.map?.locationMode,
    coordinatesEnabled,
  ]);
  const [modeOverride, setModeOverride] = useState<{
    base: string;
    mode: "address" | "coordinates" | "image";
  }>();
  const requestedMode =
    modeOverride?.base === modeKey
      ? modeOverride.mode
      : field.map?.locationMode || "address";
  const mode =
    requestedMode === "coordinates" && !coordinatesEnabled
      ? "address"
      : requestedMode;
  const queryKey = JSON.stringify([defaultsKey, answers]);
  const [override, setOverride] = useState<{ base: string; text: string }>();
  const query =
    override?.base === queryKey
      ? override.text
      : mapDefaultAddress(field, values, answers) ||
        (() => {
          const saved = values[field.id];
          if (
            saved &&
            typeof saved === "object" &&
            !Array.isArray(saved) &&
            saved.location &&
            typeof saved.location === "object" &&
            "label" in saved.location
          )
            return String(saved.location.label || "");
          return "";
        })();
  const latitude = mapPartValue(field, values, "latitude", answers),
    longitude = mapPartValue(field, values, "longitude", answers);
  const coordinateLocation = coordinatesLocation(latitude, longitude);
  const scope = JSON.stringify([
    requestScope,
    defaultsKey,
    mode,
    query,
    latitude,
    longitude,
  ]);
  const [result, setResult] = useState<MapSearchResult>(),
    [selected, setSelected] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const pending = useRef<AbortController>(undefined),
    live = useRef({ scope, services, disabled, onChange, field });
  live.current = { scope, services, disabled, onChange, field };
  useEffect(() => {
    pending.current?.abort();
    pending.current = undefined;
    setBusy(false);
    setResult(undefined);
    setSelected("");
    setError("");
    return () => pending.current?.abort();
  }, [scope, disabled, services]);
  const setPart = (key: string, value: string) =>
    setPartOverride({
      base: defaultsKey,
      answers: { ...answers, [key]: value },
    });
  const locked = (key: string) =>
    !field.map?.disabledParts?.includes(key) &&
    (!!field.map?.linked?.[key] || field.map?.fixed?.[key] !== undefined);
  const addressLocked = addressParts.some(({ key }) => locked(key));
  const run = async (create: boolean) => {
    const start = live.current;
    if (start.disabled || mode === "image") return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError("");
    const current = () =>
      !controller.signal.aborted &&
      live.current.scope === start.scope &&
      live.current.services === start.services &&
      !live.current.disabled;
    try {
      if (create) {
        const location: MapLocation | undefined =
          mode === "coordinates"
            ? coordinateLocation
            : result?.locations.find((item) => item.id === selected);
        const providerId =
          mode === "coordinates"
            ? start.services.provider?.id
            : result?.provider.id;
        if (!location || !providerId)
          throw Error(
            mode === "coordinates"
              ? "Enter valid coordinates and configure a map provider."
              : "Choose a map location first.",
          );
        const generated = await start.services.create(
          location,
          providerId,
          start.field,
          controller.signal,
        );
        if (current())
          live.current.onChange({
            ...generated.value,
            adjustments: defaultAdjustments(),
          });
      } else {
        const found = await start.services.search(query, controller.signal);
        if (current()) {
          setResult(found);
          setSelected(found.locations[0]?.id || "");
        }
      }
    } catch (error) {
      if (current()) setError((error as Error).message);
    } finally {
      if (pending.current === controller && current()) {
        setBusy(false);
        pending.current = undefined;
      }
    }
  };
  const renderPart = (
    part: { key: string; label: string },
    numeric = false,
  ) => (
    <label key={part.key} style={{ display: "block" }}>
      <span>{part.label}</span>
      <input
        aria-label={`${field.label} ${part.label}`}
        type={numeric ? "number" : "text"}
        step={numeric ? "any" : undefined}
        min={
          numeric ? (part.key === "latitude" ? -85.05112878 : -180) : undefined
        }
        max={
          numeric ? (part.key === "latitude" ? 85.05112878 : 180) : undefined
        }
        value={mapPartValue(field, values, part.key, answers)}
        maxLength={numeric ? undefined : 500}
        disabled={busy || disabled || locked(part.key)}
        onChange={(e) => setPart(part.key, e.target.value)}
      />
      {field.map?.linked?.[part.key] && <small>Linked to another field.</small>}
      {field.map?.fixed?.[part.key] !== undefined && (
        <small>Fixed answer.</small>
      )}
    </label>
  );
  return (
    <fieldset disabled={disabled} className="map-picker">
      <legend>Generate {field.label}</legend>
      <p className="field-help">{services.notice}</p>
      <label htmlFor={`map-source-${field.id}`}>Map source</label>
      <Select
        id={`map-source-${field.id}`}
        value={mode}
        disabled={busy || disabled}
        onChange={(e) =>
          setModeOverride({
            base: modeKey,
            mode: e.target.value as typeof mode,
          })
        }
      >
        <option value="address">Address</option>
        {coordinatesEnabled && (
          <option value="coordinates">Latitude / longitude</option>
        )}
      </Select>
      {mode === "image" ? (
        <p className="field-help">
          Use the image upload below to supply your own map artwork.
        </p>
      ) : mode === "coordinates" ? (
        <>
          {coordinateParts.map((part) => renderPart(part, true))}
          {!services.provider && (
            <p className="field-help">
              Direct coordinates are unavailable for this map connection. Choose
              Address or upload an image.
            </p>
          )}
          {!coordinateLocation && (latitude || longitude) && (
            <p className="field-help">
              Enter latitude between -85.05112878 and 85.05112878 and longitude
              between -180 and 180.
            </p>
          )}
          <button
            type="button"
            disabled={
              disabled || busy || !coordinateLocation || !services.provider
            }
            onClick={() => void run(true)}
          >
            Generate map from coordinates
          </button>
        </>
      ) : (
        <>
          <details>
            <summary>Address fields</summary>
            {addressParts
              .filter((part) => !field.map?.disabledParts?.includes(part.key))
              .map((part) => renderPart(part))}
          </details>
          <label htmlFor={`map-query-${field.id}`}>Address or place</label>
          <input
            id={`map-query-${field.id}`}
            value={query}
            maxLength={500}
            readOnly={addressLocked}
            disabled={busy || disabled}
            onChange={(e) =>
              setOverride({ base: queryKey, text: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (query.trim() && !busy) void run(false);
              }
            }}
          />
          {addressLocked && (
            <small>
              Edit the available address fields above; linked and fixed parts
              stay unchanged.
            </small>
          )}
          <button
            type="button"
            disabled={disabled || busy || !query.trim()}
            onClick={() => void run(false)}
          >
            Find address
          </button>
          {result && (
            <>
              <p className="field-help">{result.provider.notice}</p>
              {result.locations.length ? (
                <>
                  <label htmlFor={`map-location-${field.id}`}>
                    Map location
                  </label>
                  <Select
                    id={`map-location-${field.id}`}
                    value={selected}
                    disabled={busy || disabled}
                    onChange={(e) => setSelected(e.target.value)}
                  >
                    {result.locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.label}
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    disabled={disabled || busy || !selected}
                    onClick={() => void run(true)}
                  >
                    Use this map
                  </button>
                </>
              ) : (
                <p role="status">No matching locations.</p>
              )}
            </>
          )}
        </>
      )}
      {busy && (
        <>
          <p role="status">
            {mode === "coordinates" || result
              ? "Generating map…"
              : "Finding locations…"}
          </p>
          <button
            type="button"
            onClick={() => {
              pending.current?.abort();
              pending.current = undefined;
              setBusy(false);
            }}
          >
            Cancel map request
          </button>
        </>
      )}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
    </fieldset>
  );
}
